import { useDeferredValue, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, Plus } from "lucide-react";
import { useInvoices, type StatusFilter } from "@/api/invoices";
import { InvoiceStatusBadges } from "@/components/invoices/InvoiceStatusBadges";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { buttonClass } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, pickTab, type TabItem } from "@/components/ui/tabs";
import { formatDate, formatMoney } from "@/lib/format";

const TABS = ["all", "draft", "unpaid", "overdue", "paid", "cancelled"] as const;
type StatusTab = (typeof TABS)[number];
const TAB_ITEMS: TabItem<StatusTab>[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "unpaid", label: "Unpaid" },
  { id: "overdue", label: "Overdue" },
  { id: "paid", label: "Paid" },
  { id: "cancelled", label: "Cancelled" },
];

export function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const tab = pickTab(params.get("status"), TABS, "all");
  const [query, setQuery] = useState("");
  const search = useDeferredValue(query.trim());
  const status: StatusFilter | undefined = tab === "all" ? undefined : tab;
  const invoices = useInvoices({ status, q: search || undefined });

  const newInvoice = (
    <Link to="/invoices/new" className={buttonClass()}>
      <Plus className="h-4 w-4" aria-hidden />
      New invoice
    </Link>
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Invoices"
        description="The central register of every draft, issued and cancelled invoice."
        actions={newInvoice}
      />
      <div className="flex flex-col gap-3">
        <Tabs
          label="Invoice status"
          items={TAB_ITEMS}
          value={tab}
          onChange={(next) => setParams(next === "all" ? {} : { status: next }, { replace: true })}
        />
        <Input
          type="search"
          placeholder="Search number, vessel, voyage or client"
          aria-label="Search invoices"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="sm:max-w-sm"
        />
      </div>

      {invoices.isPending ? (
        <p className="text-muted-foreground">Loading invoices…</p>
      ) : invoices.isError ? (
        <RequestError error={invoices.error} />
      ) : invoices.data.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={search || tab !== "all" ? "No invoices match" : "No invoices yet"}
          description="Create a draft for a vessel call, preview it, then issue it to give it a number."
          action={search || tab !== "all" ? undefined : newInvoice}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3 md:hidden">
            {invoices.data.map((invoice) => (
              <li key={invoice.id}>
                <Link
                  to={`/invoices/${invoice.id}`}
                  className="flex flex-col gap-1.5 rounded-lg border bg-card p-4 shadow-sm"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-primary">
                      {invoice.invoice_no ?? "Draft"}
                    </span>
                    <Badge>{invoice.client_code}</Badge>
                  </span>
                  <InvoiceStatusBadges invoice={invoice} />
                  <span className="text-sm">
                    {invoice.vessel_name ?? "—"}
                    {invoice.voyage ? ` · voyage ${invoice.voyage}` : ""}
                  </span>
                  <span className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatDate(invoice.invoice_date)}</span>
                    <span className="text-sm font-semibold text-foreground">
                      {invoice.state === "issued" && invoice.balance !== invoice.total
                        ? `${formatMoney(invoice.balance, invoice.currency)} due`
                        : formatMoney(invoice.total, invoice.currency)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="table-frame hidden md:block">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Vessel</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.data.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/invoices/${invoice.id}`} className="text-primary hover:underline">
                        {invoice.invoice_no ?? "Draft"}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap">{formatDate(invoice.invoice_date)}</td>
                    <td>{invoice.client_code}</td>
                    <td>
                      {invoice.vessel_name ?? "—"}
                      {invoice.voyage && (
                        <span className="block text-xs text-muted-foreground">
                          voyage {invoice.voyage}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {formatMoney(invoice.total, invoice.currency)}
                    </td>
                    <td className="whitespace-nowrap text-right font-medium">
                      {invoice.state === "issued" ? formatMoney(invoice.balance, invoice.currency) : "—"}
                    </td>
                    <td>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <InvoiceStatusBadges invoice={invoice} />
                        {invoice.source === "legacy_import" && <Badge variant="muted">Imported</Badge>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
